"use client"

import { useMemo, useState } from "react"
import { Loader, Search } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import placeholder from "../../../util/placeholder"
import axiosConfig, { fetcher } from "../../../util/axiosConfig"
import requestErrorHandler from "../../../util/requestErrorHandler"
import { message, Popconfirm, Skeleton, Table, Tabs, TabsProps } from "antd"
import BreadCrumb from "../../../componentsV2/BreadCrumb"
import { VEHICLE_CATEGORY } from "../../../util/constants";
import { ColumnsType } from "antd/es/table"
import useSWR from "swr";

interface CategoryDetailsProps {
  code: string;
  createdAt: string;
  description: string;
  id: string;
  image: string;
  materials: any[]
  name: string;
  updatedAt: string;
}

export default function CategoryDetails() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const categoryState: CategoryDetailsProps = location.state || {};
  const [status, setStatus] = useState(VEHICLE_CATEGORY.ACTIVE)
  const [searchQuery, setSearchQuery] = useState("")
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);

  const { data, isLoading } = useSWR(`newAdmin/vehicles/category/${categoryId}/?status=${status}`, fetcher);
  const { data: categoryData, isLoading: isCategoryLoading } = useSWR(`newAdmin/one-truck-category/?id=${categoryId}`, fetcher);

  const category: CategoryDetailsProps = useMemo(() => {
    return categoryData?.truckCategory ?? categoryState;
  }, [categoryData]);

  const vehicles: Vehicle[] = useMemo(() => {
    return data?.data ?? [];
  }, [data])

  const columns: ColumnsType<Vehicle> = [
    {
      title: "Reference",
      dataIndex: "referenceId",
      key: "referenceId",
      render: (_, record, index) => (
        <Link to={"/admin/trucks/" + record?.id + "/truck"}>{record?.referenceId}</Link>
      ),
    },
    {
      title: "Plate Number",
      dataIndex: "plateNumber",
      key: "plateNumber",
      render: (_, record, index) => (
        <span className="text-secondary">
          {record?.regPlate}
        </span>
      ),
    },
    {
      title: "Total Trips",
      dataIndex: "totalTrips",
      key: "totalTrips",
      render: (_, record, index) => (
        <span className="text-primary">
          {record?.totalTrips}
        </span>
      ),
    },
    {
      title: "Fleet Owner",
      dataIndex: "fleetOwner",
      key: "fleetOwner",
      render: (_, record, index) => (
        <span className="text-primary">
          {record?.fleetOwner?.companyName}
        </span>
      ),
    },
    {
      title: "On Duty",
      dataIndex: "onDuty",
      key: "onDuty",
      render: (_, record, index) => (
        <div className={`size-3 rounded-full ${record?.onDuty ? 'bg-primary' : 'bg-red-500'}`} />
      )
    },
  ]

  const items: TabsProps["items"] = [
    {
      key: VEHICLE_CATEGORY.ACTIVE,
      label: "Active",
      children: isLoading ? <Skeleton active paragraph={{ rows: 8 }} /> : <Table
        columns={columns}
        dataSource={vehicles}
        rowKey="id"
        onRow={(record) => {
          return {
            onClick: () => {
              navigate(`/admin/trucks/${record.id}/truck`, { state: record });
            },
          };
        }}
      />,
    },
    {
      key: VEHICLE_CATEGORY.INACTIVE,
      label: "Inactive",
      children: isLoading ? <Skeleton active paragraph={{ rows: 8 }} /> : <Table
        columns={columns}
        dataSource={vehicles}
        rowKey="id"
        onRow={(record) => {
          return {
            onClick: () => {
              navigate(`/admin/trucks/${record.id}/truck`, { state: record });
            },
          };
        }}
      />,
    }
  ];

  const deleteCategory = async () => {
    try {
      setIsDeletingCategory(true);
      await axiosConfig.delete(`newAdmin/truck-category-delete?id=${categoryId}`);
      message.success("Truck category deleted successfully!");
      navigate("/admin/trucks");
    } catch (error) {
      requestErrorHandler(error);
    } finally {
      setIsDeletingCategory(false);
    }
  }

  const SearchVehicles = (<div className="relative w-full sm:w-auto">
    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
    <Input
      placeholder="Search"
      className="pl-8 w-full sm:w-[300px]"
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
    />
  </div>);

  return (
    <div className="min-h-screen bg-primary-accent">
      <div>
        {/* Main content */}
        <main className="flex-1 p-6 h-full">
          <div className="flex flex-1 h-full flex-col lg:flex-row gap-6">
            {/* Left section - Vehicles list */}
            <div className="flex-1 bg-white  h-full rounded-lg shadow-sm border p-6">
              {/* Back button and title */}
              <BreadCrumb label="Category Vehicles" showBack />

              {/* Tabs and search */}
              <div className="overflow-x-auto">
                <Tabs
                  defaultActiveKey={VEHICLE_CATEGORY.ACTIVE}
                  items={items}
                  tabBarExtraContent={SearchVehicles}
                  onChange={(key) => setStatus(key)}
                />
              </div>
              {/* <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div className="flex">
                  <Button
                    variant="ghost"
                    onClick={() => setActiveTab("active")}
                    className={`bg-transparent ${activeTab === "active" && "bg-[#0063CF4D]"
                      } px-8 py-4 text-primary  `}
                  >
                    Active
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setActiveTab("inactive")}
                    className={`bg-transparent ${activeTab === "inactive" && "bg-[#0063CF4D]"
                      }  px-8 py-4 text-primary `}
                  >
                    Inactive
                  </Button>
                </div>

                <div className="relative w-full sm:w-auto">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search"
                    className="pl-8 w-full sm:w-[300px]"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div> */}

              {/* Vehicles table */}
              {/* <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Vehicle Name</th>
                      <th className="text-left py-3 px-4 font-medium">Plate Number</th>
                      <th className="text-left py-3 px-4 font-medium">Total Trips</th>
                      <th className="text-left py-3 px-4 font-medium">Fleet Owner Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicles?.length > 0 ? (
                      vehicles?.map((vehicle) => (
                        <tr key={vehicle.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">{vehicle.name}</td>
                          <td className="py-3 px-4">{vehicle.plateNumber}</td>
                          <td className="py-3 px-4">{vehicle.totalTrips}</td>
                          <td className="py-3 px-4">{vehicle.fleetOwnerName}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center py-16">
                          <p className="text-gray-500 font-medium">No Vehicle in this Category yet</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div> */}
            </div>
            {/* Right section - Category details */}
            {category && (
              <div className="lg:w-[400px] bg-white rounded-lg shadow-sm border p-6">
                <div className="flex justify-end">
                <Popconfirm
                  okText="Yes"
                  cancelText="No"
                  cancelButtonProps={{ type: "primary" }}
                  okButtonProps={{ danger: true, type: "default" }}
                  onConfirm={deleteCategory}
                  title="Are you sure you want to remove this Category?"
                >
                  <Button className="bg-secondary hover:text-white mb-2">
                    Delete Category
                    {isDeletingCategory && <Loader className="animate-spin" />}
                  </Button>
                </Popconfirm>                
                </div>
                <div className="flex flex-col items-center mb-6">
                  <img
                    src={category?.image || placeholder}
                    alt={category?.name}
                    className="w-full max-w-[300px] h-auto object-contain mb-4"
                  />
                  {/* <h2 className="text-xl font-bold">{category.capacity || "30 Tons"}</h2> */}
                </div>

                <p className="text-gray-600 mb-6">
                  {category?.description}
                </p>

                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">{category?.name}</h3>
                  {/* <Button variant="outline" size="sm" className="text-blue-600">
                    Collapse
                  </Button> */}
                </div>

                <div className="space-y-4">
                  <h3 className="text-base font-semibold">Materials</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {category?.materials && category?.materials.length > 0 && (
                      category?.materials.map((material: any, index: number) => (
                        <div key={index} className="relative w-[135px] h-[46px] bg-gray-100 rounded overflow-hidden">
                          <img
                            src={material.image || placeholder}
                            alt={material.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <span className="text-white font-medium capitalize">{material.name}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={() => navigate(`/admin/trucks/edit/${categoryId}`, { state: category })}
                    className="text-white"
                  >
                    Edit Category
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

